import TableList from "app/components/organisms/TableList";
import { useRef } from "react";
import { AuditLogModel } from "app/services/api/types/auditLogs/models/AuditLogModel";
import { ColumnModel } from "app/services/api/types/common/models/ColumnModel";
import { View } from "ui/atoms/view";
import Pagination from "ui/molecules/pagination/Pagination";
import {
  useDownloadAuditLogMutation,
  useGenerateExportAuditLogMutation,
  useGetAuditLogsQuery,
  useLazyGetGeneratedExportsQuery,
} from "app/services/api/auditLogsApi";
import usePagination from "ui/molecules/pagination/usePagination";
import { format, parse } from "date-fns";
import { capitalize } from "ui/utils/textUtils";
import { MobileTableCard } from "app/components/organisms/MobileTableCard";
import { Button, ButtonIcon, ButtonText } from "ui/gluestack/button";
import { ChevronRightIcon } from "ui/gluestack/icon";
import AuditLogDetailsModal, {
  AuditLogDetailsModalRef,
} from "../modals/AuditLogDetailsModal";
import { AuditLogFilterComponent } from "../molecules/AuditLogFilterComponent";
import { useAuditLogFilterParams } from "../hooks/useAuditLogFilterParams";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import AuditLogExportModal, {
  AuditLogExportModalRef,
} from "../modals/AuditLogExportModal";
import { ReportStatusEnum } from "../../../services/api/types/reports/enums/ReportStatusEnum";

export function AuditLogsListingTemplate() {
  const { currentPage, handlePageChange } = usePagination();
  const filter = useAuditLogFilterParams();
  const { data, isFetching } = useGetAuditLogsQuery({
    page: currentPage,
    filter,
  });
  const [generateExportAuditLog] = useGenerateExportAuditLogMutation();
  const [downloadAuditLog] = useDownloadAuditLogMutation();
  const detailsRef = useRef<AuditLogDetailsModalRef>(null);
  const exportRef = useRef<AuditLogExportModalRef>(null);
  const { showNewToast, showErrorToast } = useDisplayToast();

  const [getGeneratedExports] = useLazyGetGeneratedExportsQuery();

  const columns: ColumnModel<AuditLogModel>[] = [
    {
      title: "Date & Time",
      dataKey: "log_date",
      transform: (data: string) => {
        return format(
          parse(data, "yyyy-MM-dd HH:mm:ss", new Date()),
          "dd/MM/yyyy HH:mm:ss",
        );
      },
    },
    { title: "User", dataKey: "user_name" },
    { title: "Model", dataKey: "model_display_name" },
    {
      title: "Action",
      dataKey: "type",
      transform: (data: string) => capitalize(data),
    },
    { title: "IP Address", dataKey: "ip" },
    {
      colSpan: 1,
      renderCell(item) {
        return (
          <Button
            variant="link"
            size="sm"
            onPress={() => detailsRef.current?.show(item)}
          >
            <ButtonText>View Details</ButtonText>
            <ButtonIcon as={ChevronRightIcon} />
          </Button>
        );
      },
    },
  ];

  const handleExportAuditLog = async (dates: {
    date_from?: string;
    date_to?: string;
  }) => {
    try {
      // eslint-disable-next-line camelcase
      const { export_id } = await generateExportAuditLog({
        filter: {
          ...filter,
          ...dates,
        },
      }).unwrap();

      showNewToast({
        title: "Export in progress",
        message: `The CSV file is being generated. It will be downloaded automatically once it's ready`,
        type: "warning",
      });

      let isComplete = false;
      while (!isComplete) {
        const response = await getGeneratedExports({
          // eslint-disable-next-line camelcase
          filter: { export_ids: [export_id] },
        }).unwrap();
        const exportItem = response?.[0];

        if (
          (exportItem && exportItem.status === ReportStatusEnum.COMPLETED) ||
          (exportItem && exportItem.status === ReportStatusEnum.FAILED)
        ) {
          isComplete = true;
        } else {
          await new Promise((resolve) => setTimeout(resolve, 3000));
        }
      }
      // eslint-disable-next-line camelcase
      const blob = await downloadAuditLog({ exportId: export_id }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // eslint-disable-next-line camelcase
      link.setAttribute("download", `audit-logs-${export_id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showErrorToast(error);
    }
  };

  return (
    <View className="flex-1">
      <View className="flex flex-row justify-end pb-2 gap-2">
        <AuditLogFilterComponent />
        <Button
          action="primary"
          size="md"
          onPress={() => exportRef.current?.show()}
        >
          <ButtonText>Export CSV</ButtonText>
        </Button>
      </View>
      <TableList
        columns={columns}
        data={data?.data || []}
        emptyMessage="No audit logs available."
        columnSpanClass="col-span-2"
        mobileRenderItem={(item) => (
          <MobileTableCard item={item} columns={columns} key={item.id} />
        )}
        onRowPress={(item) => detailsRef.current?.show(item)}
        isFetching={isFetching}
      ></TableList>

      <Pagination meta={data?.meta} onPageChange={handlePageChange} />

      <AuditLogDetailsModal ref={detailsRef} />
      <AuditLogExportModal ref={exportRef} onConfirm={handleExportAuditLog} />
    </View>
  );
}
